import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { translations } from './translations'
import { COMPANY_SLUG } from '@/config/companyProfile'

const I18nContext = createContext()

function normalizeLanguage (value) {
  if (!value) return 'fr'
  if (value === 'pt' || value === 'pt-PT' || value.startsWith('pt-')) {
    return 'pt-BR'
  }
  return value
}

export function I18nProvider ({ children }) {
  const storageKey = `${COMPANY_SLUG}_lang`
  const [language, setLanguage] = useState(() => {
    if (typeof window === 'undefined') return 'fr'
    return normalizeLanguage(localStorage.getItem(storageKey) || localStorage.getItem('lang') || 'fr')
  })

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(storageKey, language)
      document.documentElement.lang = language
    }
  }, [language, storageKey])

  const changeLanguage = value => setLanguage(normalizeLanguage(value))

  const t = (key, fallbackOrVars = '', vars = undefined) => {
    const fallback = typeof fallbackOrVars === 'string' ? fallbackOrVars : ''
    const variables = typeof fallbackOrVars === 'object' && fallbackOrVars !== null
      ? fallbackOrVars
      : vars

    const bundle = translations[language] ?? (language === 'pt' || language.startsWith('pt-') ? translations['pt-BR'] : undefined)
    const raw = key.split('.').reduce((acc, part) => acc?.[part], bundle) ?? fallback
    if (!variables || typeof raw !== 'string') {
      return raw
    }

    return raw.replace(/{{\s*(\w+)\s*}}/g, (_, name) => {
      const value = variables[name]
      return value === undefined || value === null ? '' : String(value)
    })
  }

  const value = useMemo(() => ({ t, changeLanguage, i18n: { language } }), [language])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useTranslation () {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    throw new Error('useTranslation must be used inside I18nProvider')
  }
  return ctx
}
